import { Container, FederatedPointerEvent, IDestroyOptions, Resource, Sprite, Texture, utils } from "pixi.js";
import { Event } from "event-system";

export class Button<TOptions extends ButtonOptions = ButtonOptions> extends Container {
    public readonly onDown: Event<this, FederatedPointerEvent> = new Event();
    public readonly onUp: Event<this, FederatedPointerEvent> = new Event();
    public readonly onUpOut: Event<this, FederatedPointerEvent> = new Event();
    public readonly onOut: Event<this, FederatedPointerEvent> = new Event();
    public readonly onHover: Event<this, FederatedPointerEvent> = new Event();
    public readonly onDestroy: Event<this> = new Event();

    public readonly defaultView: Sprite;

    public set enabled(value: boolean) {
        this.eventMode = value ? "static" : "none";
    }

    public get enabled(): boolean {
        return this.eventMode !== "none";
    }

    protected readonly options: TOptions;

    private isDown: boolean = false;
    private isPointerOver: boolean = false;

    constructor(options?: TOptions) {
        super();
        this.options = options;
        this.defaultView = this.addChild(new Sprite(this.options?.defaultTexture));
        this.addListeners();
        this.enabled = true;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.onDestroy.emit(this);
        this.removeListeners();
        super.destroy(options);
    }

    private addListeners(): void {
        this.on('pointerdown', this.onPointerDown, this);
        this.on('pointerup', this.onPointerUp, this);
        this.on('pointerupoutside', this.onPointerUpOutside, this);
        this.on('pointerout', this.onPointerOut, this);
        this.on('pointerover', this.onPointerOver, this);
        this.on('pointerleave', this.onPointerLeave, this);
    }

    private removeListeners(): void {
        this.off('pointerdown', this.onPointerDown, this);
        this.off('pointerup', this.onPointerUp, this);
        this.off('pointerupoutside', this.onPointerUpOutside, this);
        this.off('pointerout', this.onPointerOut, this);
        this.off('pointerover', this.onPointerOver, this);
        this.off('pointerleave', this.onPointerLeave, this);
    }

    protected handlePointerDown(e: FederatedPointerEvent): void {
        this.onDown.emit(this, e);
    }

    protected handlePointerUp(e: FederatedPointerEvent): void {
        this.onUp.emit(this, e);
    }

    protected handlePointerUpOutside(e: FederatedPointerEvent): void {
        this.onUpOut.emit(this, e);
    }

    protected handlePointerOut(e: FederatedPointerEvent): void {
        this.onOut.emit(this, e);
    }

    protected handlePointerOver(e: FederatedPointerEvent): void {
        this.onHover.emit(this, e);
    }

    private onPointerDown(e: FederatedPointerEvent): void {
        if (!this.isDown) {
            this.isDown = true;
            this.isPointerOver = true;
            this.handlePointerDown(e);
        }
    }

    private onPointerUp(e: FederatedPointerEvent): void {
        if (this.isDown) {
            this.isDown = false;
            this.isPointerOver = false;
            if (this.checkPointerOnButton(e)) {
                this.handlePointerUp(e);
            }
            else {
                this.handlePointerOut(e);
            }
        }
    }

    private onPointerUpOutside(e: FederatedPointerEvent): void {
        if (this.isDown) {
            this.isDown = false;
            this.handlePointerUpOutside(e);
        }
        if (this.isPointerOver) {
            this.isPointerOver = false;
            this.handlePointerUpOutside(e);
        }
    }

    private onPointerOut(e: FederatedPointerEvent): void {
        if (this.isPointerOver) {
            this.isPointerOver = false;
            this.handlePointerOut(e);
        }
    }

    private onPointerOver(e: FederatedPointerEvent): void {
        if (!this.isPointerOver) {
            this.isPointerOver = true;
            this.handlePointerOver(e);
        }
    }

    private onPointerLeave(e: FederatedPointerEvent): void {
        if (this.isPointerOver && !this.checkPointerOnButton(e)) {
            this.handlePointerOut(e);
        }
    }

    private checkPointerOnButton(e: FederatedPointerEvent): boolean {
        const pointerPosition = e.getLocalPosition(this);
        const bounds = this.getLocalBounds();
        return this.hitArea == null ? pointerPosition.x > 0 && pointerPosition.x < Math.abs(bounds.width)
            && pointerPosition.y > 0 && pointerPosition.y < Math.abs(bounds.height) : this.hitArea.contains(pointerPosition.x, pointerPosition.y);
    }
}

export interface ButtonOptions {
    defaultTexture: Texture<Resource>;
}